/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * AppLayout (#683, W2 Phase 1) — the navigation shell.
 *
 * Column layout: workflow top bar → breadcrumbs → [contextual sidebar | main].
 * Replaces the row layout built around the collapsible navy sidebar.
 *
 * **Routing stays the source of truth.** The mock drove navigation from an
 * in-memory trail; here the active tab and surface are derived from
 * `useLocation`, so deep links, browser history and `ProtectedRoute` keep
 * working exactly as before — the shell is presentation over the same routes.
 */

import { MAIN_PADDING } from './main-padding';
import React, { useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Box, Flex } from '@chakra-ui/react';
import { WorkflowTopBar } from './WorkflowTopBar';
import { ContextSidebar } from './ContextSidebar';
import { readRailCollapsed, writeRailCollapsed } from './rail-storage';
import { Breadcrumbs, type Crumb } from './Breadcrumbs';
import { MobileNavDrawer } from './MobileNavDrawer';
import { buildNavTabs, findNavLocation } from '../../utils/rbac';
import { useAuth, useIsMobile } from '../../hooks';

/** Surfaces reachable outside the nav tree still deserve a breadcrumb. */
const OFF_NAV_LABELS: Record<string, string> = {
  '/profile': 'My Profile',
  '/unauthorized': 'Not Authorized',
};

export const AppLayout: React.FC = () => {
  const location = useLocation();
  const isMobile = useIsMobile();
  const { can, role } = useAuth();

  // Tabs are rebuilt when permissions change — never cached across users.
  const tabs = useMemo(() => buildNavTabs(can, { showLocked: role === 'demo_user' }), [can, role]);

  const current = useMemo(() => findNavLocation(tabs, location.pathname), [tabs, location.pathname]);

  const [railCollapsed, setRailCollapsed] = useState(readRailCollapsed);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const toggleRail = () => {
    setRailCollapsed((collapsed) => {
      writeRailCollapsed(!collapsed);
      return !collapsed;
    });
  };

  /**
   * Route-derived trail (D1): `group › surface`, or just the surface when it has
   * no group. #817 will supply journey crumbs here instead.
   */
  const crumbs = useMemo<Crumb[]>(() => {
    if (current) {
      const { tab, item } = current;
      return tab.kind === 'group'
        ? [{ label: tab.group.label, path: tab.items[0].path }, { label: item.label }]
        : [{ label: item.label }];
    }
    const offNav = OFF_NAV_LABELS[location.pathname];
    return offNav ? [{ label: offNav }] : [];
  }, [current, location.pathname]);

  // Only a multi-surface group gets a contextual sidebar; a direct tab owns the
  // full width, matching how the old sidebar flattened a single visible child.
  const sidebarTab = current?.tab.kind === 'group' ? current.tab : undefined;

  return (
    /**
     * **`clip`, not `hidden`, and the difference is a defect this shell has already had.**
     *
     * `overflow: hidden` still makes an element **programmatically** scrollable — it removes the
     * scrollbar, not the scroll. So a `scrollIntoView({ block: 'start' })` anywhere inside scrolls
     * *this* box as well as `main`, and because the top bar and the footer sit outside `main`, the
     * whole chrome slides away with no scrollbar and no wheel to bring it back. Reported by Todd
     * 2026-09-09 against the expression library, whose editor hand-rolled exactly that call.
     *
     * `overflow: clip` does not create a scroll container at all, so there is nothing for a
     * descendant to scroll. The page-level fix was to use `useRevealOnOpen`; this is the same fix at
     * the level that holds it, so the fifth hand-written `scrollIntoView` cannot do it again.
     */
    <Flex direction="column" h="100vh" style={{ height: '100dvh' }} maxW="100vw" bg="bg.canvas" overflow="clip">
      <WorkflowTopBar
        tabs={tabs}
        activeTabKey={current?.tab.key}
        activePath={current?.item.path}
        isMobile={isMobile}
        onOpenMenu={() => setDrawerOpen(true)}
      />

      {isMobile && <MobileNavDrawer open={drawerOpen} tabs={tabs} onClose={() => setDrawerOpen(false)} />}

      <Breadcrumbs crumbs={crumbs} />

      <Flex flex={1} minH={0} minW={0}>
        {!isMobile && sidebarTab && (
          <ContextSidebar
            label={sidebarTab.group.label}
            items={sidebarTab.items}
            collapsed={railCollapsed}
            onToggle={toggleRail}
          />
        )}

        {/* `clip` for the reason on the outer column: this is the box whose scroll would take the
            footer with it. */}
        <Flex direction="column" flex={1} minW={0} minH={0} overflow="clip">
          <Box as="main" flex={1} minW={0} minH={0} overflowY="auto" overflowX="auto" p={MAIN_PADDING}>
            <Outlet />
          </Box>
          <Box
            as="footer"
            px={{ base: 3, md: 6 }}
            py={2}
            borderTopWidth="1px"
            borderTopColor="border.subtle"
            bg="bg.canvas"
          >
            <Box textAlign="right" fontSize="10px" color="neutral.gray" lineHeight="1.2">
              v{__APP_VERSION__} • © 2025 Adaptive Chaos Labs, LLC
            </Box>
          </Box>
        </Flex>
      </Flex>
    </Flex>
  );
};
